import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorMaster() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_name: '',
    vendor_type: ''
  });

  const fetchVendors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/masters/vendors`, {
        
      });
      setVendors(response.data);
    } catch (err) { console.error(err);
      toast.error('Failed to fetch vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        created_at: new Date().toISOString()
      };

      if (editingVendor) {
        await axios.put(`${API}/masters/vendors/${editingVendor.vendor_id}`, data, {
          
        });
        toast.success('Vendor updated successfully');
      } else {
        await axios.post(`${API}/masters/vendors`, data, {
          
        });
        toast.success('Vendor created successfully');
      }

      setDialogOpen(false);
      resetForm();
      fetchVendors();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save vendor');
    }
  };

  const handleEdit = (vendor) => {
    setEditingVendor(vendor);
    setFormData({
      vendor_id: vendor.vendor_id,
      vendor_name: vendor.vendor_name,
      vendor_type: vendor.vendor_type
    });
    setDialogOpen(true);
  };

  const handleDelete = async (vendorId) => {
    if (!window.confirm('Are you sure you want to delete this vendor?')) return;

    try {
      await axios.delete(`${API}/masters/vendors/${vendorId}`, {
        
      });
      toast.success('Vendor deleted successfully');
      fetchVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      vendor_name: '',
      vendor_type: ''
    });
    setEditingVendor(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="vendor-master-title">VENDOR MASTER</h1>
          <p className="text-sm text-gray-700 mt-2">Manage vendor information</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              data-testid="add-vendor-button"
              onClick={openCreateDialog}
              className="bg-accent text-accent-foreground hover:bg-accent-hover rounded-sm font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              ADD VENDOR
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}}>
                {editingVendor ? 'EDIT VENDOR' : 'NEW VENDOR'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor ID</Label>
                <Input
                  data-testid="vendor-id-input"
                  value={formData.vendor_id}
                  onChange={(e) => setFormData({...formData, vendor_id: e.target.value})}
                  disabled={!!editingVendor}
                  required
                  className="mt-1.5 rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor Name</Label>
                <Input
                  data-testid="vendor-name-input"
                  value={formData.vendor_name}
                  onChange={(e) => setFormData({...formData, vendor_name: e.target.value})}
                  required
                  className="mt-1.5 rounded-none"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Vendor Type</Label>
                <Select
                  value={formData.vendor_type}
                  onValueChange={(value) => setFormData({...formData, vendor_type: value})}
                  required
                >
                  <SelectTrigger data-testid="vendor-type-select" className="mt-1.5 rounded-none">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Steel">Steel</SelectItem>
                    <SelectItem value="Cutting">Cutting</SelectItem>
                    <SelectItem value="Forging">Forging</SelectItem>
                    <SelectItem value="Machining">Machining</SelectItem>
                    <SelectItem value="HT">Heat Treatment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="bg-secondary text-secondary-foreground hover:bg-secondary/80 rounded-sm"
                >
                  CANCEL
                </Button>
                <Button
                  data-testid="submit-vendor-button"
                  type="submit"
                  className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm"
                >
                  {editingVendor ? 'UPDATE' : 'CREATE'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-border shadow-sm">
        {loading ? (
          <div className="p-12 text-center text-gray-700">Loading vendors...</div>
        ) : vendors.length === 0 ? (
          <div className="p-12 text-center text-gray-700">No vendors found. Create your first vendor.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b">
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor ID</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Vendor Name</th>
                  <th className="h-10 px-4 text-left align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Type</th>
                  <th className="h-10 px-4 text-center align-middle font-medium text-muted-foreground bg-gray-50/50 uppercase text-xs tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((vendor, idx) => (
                  <tr key={vendor.vendor_id} className="border-b transition-colors hover:bg-muted/50" data-testid={`vendor-row-${idx}`}>
                    <td className="p-4 align-middle font-mono text-xs">{vendor.vendor_id}</td>
                    <td className="p-4 align-middle">{vendor.vendor_name}</td>
                    <td className="p-4 align-middle">
                      <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold border-transparent bg-slate-100 text-slate-700">
                        {vendor.vendor_type}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`edit-vendor-${idx}`}
                          onClick={() => handleEdit(vendor)}
                          className="p-2 hover:bg-accent/10 hover:text-accent transition-all rounded-sm"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-vendor-${idx}`}
                          onClick={() => handleDelete(vendor.vendor_id)}
                          className="p-2 hover:bg-error/10 hover:text-error transition-all rounded-sm"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}